import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useGetTimeAttendaceQuery } from "@/services/(my-space)/api.time-attendance";
import { formatTimeFromISO } from "@/utils/helper";
import { useServerToday } from "@/hooks/useServerToday";
import { ReportingTo } from "../ReportingTo";
import { TimeCounter } from "../TimeCounter";
import { useEffect, useRef, useState } from "react";
import { CheckInCheckoutButton } from "./components/ClockInOutButton";
import { QuickActivityButtons } from "./components/QuickActivityButtons";
import { AttendanceProgressBar } from "@/pages/(home)/_components/AttendanceProgressBar";
import { useAppSelector } from "@/hooks/useReduxHooks";
import { selectUser } from "@/store/slices/user.slice";

function useIsWrapped(ref: React.RefObject<HTMLElement | null>) {
  const [wrapped, setWrapped] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => {
      if (window.innerWidth < 1024) {
        setWrapped(false);
        return;
      }
      const [first, second] = Array.from(el.children) as HTMLElement[];
      if (first && second) setWrapped(second.offsetTop > first.offsetTop);
    };
    const ro = new ResizeObserver(check);
    ro.observe(document.documentElement);
    check();
    return () => ro.disconnect();
  }, [ref]);
  return wrapped;
}

const AttendanceTimerActionBar = () => {
  const { t } = useTranslation("my-space");
  const today = useServerToday();
  const { data, isLoading } = useGetTimeAttendaceQuery({ date: today });

  const timeCounterRef = useRef<{ pause: () => void }>(null);
  const {
    isCurrentlyCheckedIn,
    attendanceActivities,
    buttonLabel,
    buttonColor,
    buttonIcon,
    pendingExceptions,
  } = data?.data ?? {};

  const { user: profileUser } = useAppSelector(selectUser);
  const shift = profileUser?.shift ?? {
    shiftName: t("ui.default"),
    shiftStartTime: "09:00:00",
    shiftEndTime: "18:00:00",
  };

  const lastActivity = attendanceActivities?.[attendanceActivities.length - 1];
  const apiActivityId = !isCurrentlyCheckedIn
    ? (lastActivity?.quickActivityId ?? null)
    : null;

  const [pendingActivityId, setPendingActivityId] = useState<number | null>(
    null,
  );

  const activeActivityId = isCurrentlyCheckedIn
    ? pendingActivityId
    : apiActivityId;

  const prevCheckedIn = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (prevCheckedIn.current === false && isCurrentlyCheckedIn === true) {
      setPendingActivityId(null);
    }
    prevCheckedIn.current = isCurrentlyCheckedIn;
  }, [isCurrentlyCheckedIn]);

  const lastClockOutTime = attendanceActivities?.at(-1)?.clockOutTime;

  const rowRef = useRef<HTMLDivElement>(null);
  const isWrapped = useIsWrapped(rowRef);

  return (
    <div className="bg-white rounded-md border mt-4">
      <div
        ref={rowRef}
        className="flex flex-col lg:flex-row lg:flex-wrap lg:items-center"
      >
        {/* Check-in button + Timer */}
        <div
          className={`flex items-center gap-2 px-2 sm:px-4 justify-between self-stretch lg:shrink-0 py-3 ${!isWrapped ? "lg:py-0 lg:self-auto" : ""}`}
        >
          <CheckInCheckoutButton
            isCurrentlyCheckedIn={Boolean(isCurrentlyCheckedIn)}
            dataLoading={isLoading}
            buttonLabel={buttonLabel}
            buttonColor={buttonColor}
            buttonIcon={buttonIcon}
            pendingExceptions={pendingExceptions}
          />
          <TimeCounter
            ref={timeCounterRef}
            isCurrentlyCheckedIn={Boolean(isCurrentlyCheckedIn)}
            attendanceActivities={attendanceActivities || []}
          />
        </div>

        {/* Progress bar + Reporting To */}
        <div
          className={`self-stretch flex grow min-w-0 overflow-hidden border-t border-border sm:mb-3 sm:border sm:rounded-md sm:mx-4 ${!isWrapped ? "lg:mx-0 lg:me-0 lg:mb-0 lg:border-none lg:rounded-none lg:overflow-visible" : ""}`}
        >
          <div
            className={`relative flex-1 min-w-0 ${!isWrapped ? "lg:h-20 lg:border-s lg:border-e lg:border-border" : ""}`}
          >
            {/* Stacked: check-in info — always on mobile, on lg only when wrapped */}
            <div
              className={`flex items-start justify-between px-3 pt-2.5 pb-1 ${!isWrapped ? "lg:hidden" : ""}`}
            >
              <div className="flex flex-col gap-0.5 text-[0.65rem] text-muted-foreground">
                <p>{t("labels.check_in")}</p>
                <p className="text-success font-medium">
                  {formatTimeFromISO(attendanceActivities?.[0]?.clockInTime)}
                </p>
              </div>
              <div className="flex flex-col items-end gap-0.5">
                <Link
                  to="/time-attendance"
                  className="text-sm font-medium text-primary min-h-[32px] flex items-center"
                >
                  {t("ui.view_attendance")}
                </Link>
                {lastClockOutTime && (
                  <p className="text-xs font-semibold text-rose-400">
                    {t("common:shared_ui.last_check_out_label", {
                      time: formatTimeFromISO(lastClockOutTime),
                    })}
                  </p>
                )}
              </div>
            </div>

            {/* Wide: check-in label overlay — lg only when not wrapped */}
            {!isWrapped && (
              <div className="hidden lg:flex absolute start-4 top-5 flex-col gap-3 text-[0.65rem] text-muted-foreground z-10 pointer-events-none">
                <p>{t("labels.check_in")}</p>
                <p className="text-success">
                  {formatTimeFromISO(attendanceActivities?.[0]?.clockInTime)}
                </p>
              </div>
            )}

            {/* Wide: View Attendance overlay — lg only when not wrapped */}
            {!isWrapped && (
              <div className="hidden lg:flex absolute end-4 top-3 flex-col gap-4 text-end z-10">
                <p className="text-sm font-medium text-primary hover:underline">
                  <Link to="/time-attendance">
                    {t("labels.view_attendance")}
                  </Link>
                </p>
                {lastClockOutTime && (
                  <p className="text-xs font-semibold text-rose-400">
                    {t("common:shared_ui.last_check_out_label", {
                      time: formatTimeFromISO(lastClockOutTime),
                    })}
                  </p>
                )}
              </div>
            )}

            {/* Progress bar */}
            <div
              className={`h-16 lg:min-w-[500px] ${!isWrapped ? "lg:h-full" : ""}`}
            >
              <AttendanceProgressBar
                date={today}
                activities={attendanceActivities || []}
                shift={shift}
                pendingExceptions={pendingExceptions}
              />
            </div>
          </div>
          <ReportingTo />
        </div>
      </div>

      <QuickActivityButtons
        value={activeActivityId}
        onChange={setPendingActivityId}
        isCheckedIn={Boolean(isCurrentlyCheckedIn)}
        activeStartTime={
          !isCurrentlyCheckedIn
            ? (lastActivity?.quickActivityStartTime ??
              lastActivity?.clockOutTime ??
              null)
            : null
        }
      />
    </div>
  );
};

export default AttendanceTimerActionBar;
